import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';

import { getMasterProjects, getSubProjects, useGetProjectsQuery } from 'features/projects';
import { ChevronLeft, ChevronRight, CloudUpload, Download, HelpCircle, Trash2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import {
  BulkImportUserAssignment,
  BulkImportUsersRowError,
  BulkImportUsersSuccessResponse,
  BulkImportUsersValidationResponse,
  IdentityOccupancyEmail,
  IdentityOccupancyProject,
  useBulkImportUsersMutation,
  useGetAssignableRolesQuery,
  useGetIdentityOccupancyQuery,
} from '../services/users.api';
import { downloadBulkCreatedUsersExcel } from '../utils/downloadBulkCreatedUsersExcel';
import { downloadUsersBulkTemplate, parseUsersBulkFile, ParsedBulkUserRow } from '../utils/parseUsersBulkFile';

const STEPS = ['Archivo', 'Vista previa'] as const;

const NO_ROLE_SELECT_VALUE = 'none';
const NO_SUBPROJECT_SELECT_VALUE = 'none';

interface RowBulkMeta {
  roleIds: number[];
  subProjectIds: number[];
}

const emptyRowMeta = (): RowBulkMeta => ({ roleIds: [], subProjectIds: [] });

const occupancyProjectLabel = (project: IdentityOccupancyProject): string =>
  project.isActive ? project.name : `${project.name} (inactivo)`;

const OccupancyProjectsCell = ({
  occupancy,
  isLoading,
  currentProjectId,
}: {
  occupancy: IdentityOccupancyEmail | undefined;
  isLoading: boolean;
  currentProjectId: number;
}): ReactNode => {
  if (isLoading) {
    return <Skeleton className="h-5 w-28" />;
  }
  if (!occupancy || !occupancy.identityExists) {
    return <span className="text-text-subtle">—</span>;
  }

  const otherProjects = occupancy.projects.filter((project) => project.id !== currentProjectId);

  return (
    <div className="flex flex-wrap gap-1">
      {occupancy.occupiedOnCurrentProject ? <Badge variant="destructive">Ya está en este proyecto</Badge> : null}
      {otherProjects.map((project) => (
        <Badge key={project.id ?? 'admin'} variant={project.isActive ? 'secondary' : 'outline'}>
          {occupancyProjectLabel(project)}
        </Badge>
      ))}
      {!occupancy.occupiedOnCurrentProject && otherProjects.length === 0 ? (
        <span className="text-sm text-text-subtle">Ya tiene cuenta</span>
      ) : null}
    </div>
  );
};

function stepChipClass(idx: number, active: number): string {
  if (idx === active) return 'bg-primary text-primary-foreground';
  if (idx < active) return 'bg-success-light text-success';
  return 'bg-bg-surface-alt text-text-muted';
}

function isValidationErrorResponse(data: unknown): data is BulkImportUsersValidationResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'errorType' in data &&
    (data as BulkImportUsersValidationResponse).errorType === 'validation_error' &&
    'errors' in data
  );
}

function isSuccessResponse(data: unknown): data is BulkImportUsersSuccessResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    (data as { status: string }).status === 'ok' &&
    'createdUsers' in data &&
    Array.isArray((data as BulkImportUsersSuccessResponse).createdUsers) &&
    'failed' in data &&
    typeof (data as BulkImportUsersSuccessResponse).failed === 'number'
  );
}

interface BulkUserImportWizardProps {
  projectId: number;
  onFinished?: () => void;
}

const BulkUserImportWizard = ({ projectId, onFinished }: BulkUserImportWizardProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<ParsedBulkUserRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<BulkImportUsersRowError[]>([]);
  const [fieldGuideOpen, setFieldGuideOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [bulkImportSummary, setBulkImportSummary] = useState<BulkImportUsersSuccessResponse | null>(null);
  const [rowMeta, setRowMeta] = useState<Record<number, RowBulkMeta>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(() => new Set());
  const [roleToApply, setRoleToApply] = useState<string>(NO_ROLE_SELECT_VALUE);
  const [subProjectToApply, setSubProjectToApply] = useState<string>(NO_SUBPROJECT_SELECT_VALUE);

  const { data: projects = [] } = useGetProjectsQuery();
  const { data: assignableRolesResponse } = useGetAssignableRolesQuery();
  const [bulkImportUsers, { isLoading: isSubmitting }] = useBulkImportUsersMutation();

  const occupancyEmails = useMemo(() => {
    const seen = new Set<string>();
    const unique: string[] = [];
    previewRows.forEach((row) => {
      const email = row.email.trim().toLowerCase();
      if (!email || seen.has(email)) {
        return;
      }
      seen.add(email);
      unique.push(email);
    });
    unique.sort();
    return unique;
  }, [previewRows]);

  const {
    data: occupancyData,
    isLoading: isOccupancyLoading,
    isError: isOccupancyError,
  } = useGetIdentityOccupancyQuery(
    { projectId, emails: occupancyEmails },
    { skip: activeStep !== 1 || occupancyEmails.length === 0 || bulkImportSummary !== null }
  );

  const occupancyByEmail = useMemo(() => {
    const map = new Map<string, IdentityOccupancyEmail>();
    occupancyData?.emails.forEach((row) => {
      map.set(row.email, row);
    });
    return map;
  }, [occupancyData]);

  const occupiedOnCurrentProjectCount = useMemo(
    () => occupancyData?.emails.filter((row) => row.occupiedOnCurrentProject).length ?? 0,
    [occupancyData]
  );

  const existingOnOtherProjectsCount = useMemo(
    () => occupancyData?.emails.filter((row) => row.identityExists && !row.occupiedOnCurrentProject).length ?? 0,
    [occupancyData]
  );

  const selectedMasterLabel = useMemo(() => {
    const master = getMasterProjects(projects).find((p) => p.id === projectId);
    return master ? `${master.name} (ID: ${master.id})` : `ID: ${projectId}`;
  }, [projects, projectId]);

  const assignableRoles = useMemo(() => assignableRolesResponse?.roles ?? [], [assignableRolesResponse]);

  const subProjectOptions = useMemo(() => getSubProjects(projects, projectId), [projects, projectId]);

  const roleById = useMemo(() => {
    const m = new Map<number, string>();
    assignableRoles.forEach((r) => {
      m.set(r.id, r.displayName);
    });
    return m;
  }, [assignableRoles]);

  const subProjectNameById = useMemo(() => {
    const m = new Map<number, string>();
    subProjectOptions.forEach((p) => {
      m.set(p.id, p.name);
    });
    return m;
  }, [subProjectOptions]);

  const allPreviewRowsHaveAtLeastOneRole = useMemo(() => {
    if (previewRows.length === 0) return true;
    return previewRows.every((row) => (rowMeta[row.id] ?? emptyRowMeta()).roleIds.length > 0);
  }, [previewRows, rowMeta]);

  const applyRoleToSelected = useCallback(() => {
    if (roleToApply === NO_ROLE_SELECT_VALUE || selectedRowIds.size === 0) return;
    const roleId = Number(roleToApply);
    if (!Number.isFinite(roleId)) return;
    setRowMeta((prev) => {
      const next = { ...prev };
      selectedRowIds.forEach((rowId) => {
        const cur = next[rowId] ?? emptyRowMeta();
        next[rowId] = {
          ...cur,
          roleIds: cur.roleIds.includes(roleId) ? cur.roleIds : [...cur.roleIds, roleId],
        };
      });
      return next;
    });
  }, [roleToApply, selectedRowIds]);

  const applySubprojectToSelected = useCallback(() => {
    if (subProjectToApply === NO_SUBPROJECT_SELECT_VALUE || selectedRowIds.size === 0) return;
    const subId = Number(subProjectToApply);
    if (!Number.isFinite(subId)) return;
    setRowMeta((prev) => {
      const next = { ...prev };
      selectedRowIds.forEach((rowId) => {
        const cur = next[rowId] ?? emptyRowMeta();
        next[rowId] = {
          ...cur,
          subProjectIds: cur.subProjectIds.includes(subId) ? cur.subProjectIds : [...cur.subProjectIds, subId],
        };
      });
      return next;
    });
  }, [selectedRowIds, subProjectToApply]);

  const clearRolesForSelected = useCallback(() => {
    if (selectedRowIds.size === 0) return;
    setRowMeta((prev) => {
      const next = { ...prev };
      selectedRowIds.forEach((rowId) => {
        const cur = next[rowId] ?? emptyRowMeta();
        next[rowId] = { ...cur, roleIds: [] };
      });
      return next;
    });
  }, [selectedRowIds]);

  const clearSubprojectsForSelected = useCallback(() => {
    if (selectedRowIds.size === 0) return;
    setRowMeta((prev) => {
      const next = { ...prev };
      selectedRowIds.forEach((rowId) => {
        const cur = next[rowId] ?? emptyRowMeta();
        next[rowId] = { ...cur, subProjectIds: [] };
      });
      return next;
    });
  }, [selectedRowIds]);

  const toggleRowSelection = useCallback((rowId: number, checked: boolean | 'indeterminate') => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (checked === true) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);

  const handleHeaderSelectAll = useCallback(
    (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        setSelectedRowIds(new Set(previewRows.map((r) => r.id)));
      } else {
        setSelectedRowIds(new Set());
      }
    },
    [previewRows]
  );

  useEffect(() => {
    setActiveStep(0);
    setSelectedFile(null);
    setPreviewRows([]);
    setParseError(null);
    setSubmitError(null);
    setRowErrors([]);
    setRowMeta({});
    setSelectedRowIds(new Set());
    setRoleToApply(NO_ROLE_SELECT_VALUE);
    setSubProjectToApply(NO_SUBPROJECT_SELECT_VALUE);
    setBulkImportSummary(null);
  }, [projectId]);

  const resetFile = useCallback(() => {
    setSelectedFile(null);
    setPreviewRows([]);
    setParseError(null);
    setSubmitError(null);
    setRowErrors([]);
    setRowMeta({});
    setSelectedRowIds(new Set());
    setRoleToApply(NO_ROLE_SELECT_VALUE);
    setSubProjectToApply(NO_SUBPROJECT_SELECT_VALUE);
    setBulkImportSummary(null);
  }, []);

  const resetAll = useCallback(() => {
    setActiveStep(0);
    resetFile();
    onFinished?.();
  }, [onFinished, resetFile]);

  const processFile = useCallback(async (file: File) => {
    const okExt = /\.(xls|xlsx)$/i.test(file.name);
    if (!okExt) {
      setParseError('Usa un archivo Excel (.xls o .xlsx)');
      return;
    }
    setIsParsing(true);
    setParseError(null);
    setSubmitError(null);
    setRowErrors([]);
    try {
      setSelectedFile(file);
      const rows = await parseUsersBulkFile(file);
      setPreviewRows(rows);
      setRowMeta({});
      setSelectedRowIds(new Set());
      setRoleToApply(NO_ROLE_SELECT_VALUE);
      setSubProjectToApply(NO_SUBPROJECT_SELECT_VALUE);
      setBulkImportSummary(null);
    } catch (err) {
      setSelectedFile(null);
      setPreviewRows([]);
      setParseError(err instanceof Error ? err.message : 'No se pudo leer el archivo');
    } finally {
      setIsParsing(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        void processFile(file);
      }
    },
    [processFile]
  );

  const handleFileInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void processFile(file);
      }
      e.target.value = '';
    },
    [processFile]
  );

  const handleBack = useCallback(() => {
    if (activeStep === 1) {
      setSubmitError(null);
      setRowErrors([]);
    }
    setActiveStep((s) => Math.max(0, s - 1));
  }, [activeStep]);

  const handleNext = useCallback(() => {
    if (activeStep === 0 && (!selectedFile || previewRows.length === 0)) {
      setParseError('Carga un archivo válido con al menos una fila');
      return;
    }
    setParseError(null);
    setActiveStep((s) => s + 1);
  }, [activeStep, selectedFile, previewRows.length]);

  const handleSubmit = useCallback(async () => {
    if (!selectedFile || previewRows.length === 0 || !allPreviewRowsHaveAtLeastOneRole) return;
    setSubmitError(null);
    setRowErrors([]);
    const assignments: BulkImportUserAssignment[] = previewRows.map((row) => {
      const meta = rowMeta[row.id] ?? emptyRowMeta();
      return {
        email: row.email,
        roleIds: meta.roleIds,
        subProjectIds: meta.subProjectIds,
      };
    });
    try {
      const result = await bulkImportUsers({
        projectId,
        usersFile: selectedFile,
        assignments,
      }).unwrap();
      if (isSuccessResponse(result)) {
        setBulkImportSummary(result);
        setRowErrors([]);
        setSubmitError(null);
      }
    } catch (error: unknown) {
      const errObj = error as { status?: number; data?: unknown };
      const { data } = errObj;
      if (errObj.status === 422 && isValidationErrorResponse(data)) {
        setRowErrors(data.errors);
        setSubmitError(data.message);
        return;
      }
      if (typeof data === 'object' && data !== null && 'message' in data) {
        setSubmitError(String((data as { message: unknown }).message));
        return;
      }
      setSubmitError('No se pudo completar la carga. Intenta de nuevo.');
    }
  }, [allPreviewRowsHaveAtLeastOneRole, bulkImportUsers, projectId, previewRows, rowMeta, selectedFile]);

  const allRowsSelected = previewRows.length > 0 && previewRows.every((r) => selectedRowIds.has(r.id));
  const someRowsSelected = previewRows.some((r) => selectedRowIds.has(r.id));
  let headerCheckboxState: boolean | 'indeterminate' = false;
  if (allRowsSelected) {
    headerCheckboxState = true;
  } else if (someRowsSelected) {
    headerCheckboxState = 'indeterminate';
  }

  const bulkCreateUsersDisabled =
    !selectedFile ||
    previewRows.length === 0 ||
    isSubmitting ||
    bulkImportSummary !== null ||
    !allPreviewRowsHaveAtLeastOneRole;

  const bulkCreateTooltipForMissingRoles =
    Boolean(selectedFile) &&
    previewRows.length > 0 &&
    !isSubmitting &&
    bulkImportSummary === null &&
    !allPreviewRowsHaveAtLeastOneRole;

  const createUsersButtonMissingRolesStyle =
    !allPreviewRowsHaveAtLeastOneRole && previewRows.length > 0 && !isSubmitting;

  const createUsersActionButton = (
    <Button
      type="button"
      variant={createUsersButtonMissingRolesStyle ? 'secondary' : 'default'}
      className={createUsersButtonMissingRolesStyle ? 'border-border text-text-muted shadow-none' : undefined}
      disabled={bulkCreateUsersDisabled}
      onClick={() => {
        void handleSubmit();
      }}
    >
      {isSubmitting ? 'Creando…' : 'Crear usuarios'}
    </Button>
  );

  let stepFooterTrailingAction: ReactNode;
  if (activeStep < STEPS.length - 1) {
    stepFooterTrailingAction = (
      <Button type="button" onClick={handleNext} className="gap-1">
        Siguiente
        <ChevronRight className="size-4" />
      </Button>
    );
  } else if (bulkCreateTooltipForMissingRoles) {
    stepFooterTrailingAction = (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-not-allowed rounded-lg">{createUsersActionButton}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-center">
            Asigna al menos un rol a cada fila antes de crear usuarios.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  } else {
    stepFooterTrailingAction = createUsersActionButton;
  }

  return (
    <div className="rounded-xl border border-border-subtle bg-bg-surface p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-text-default m-0 mb-1 font-display">Carga masiva de usuarios</h3>
      <p className="text-sm text-text-subtle m-0 mb-4">
        Proyecto: <span className="font-medium text-text-default">{selectedMasterLabel}</span>
      </p>

      <ol className="flex flex-wrap gap-2 mb-6">
        {STEPS.map((label, idx) => (
          <li
            key={label}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${stepChipClass(idx, activeStep)}`}
          >
            {idx + 1}. {label}
          </li>
        ))}
      </ol>

      {bulkImportSummary && (
        <div
          className="mb-6 rounded-lg border border-border-subtle bg-bg-surface-alt/60 p-4 space-y-3"
          role="region"
          aria-label="Resultado de la carga masiva"
        >
          <p className="text-sm font-medium text-text-default m-0">
            Se crearon {bulkImportSummary.created} usuario(s) correctamente.
          </p>
          {bulkImportSummary.failed > 0 ? (
            <p className="text-sm text-text-default m-0">No se pudieron crear {bulkImportSummary.failed} usuario(s).</p>
          ) : null}
          {bulkImportSummary.errors.length > 0 ? (
            <ul className="text-xs text-destructive list-disc pl-5 m-0 max-h-40 overflow-y-auto space-y-1">
              {bulkImportSummary.errors.map((re) => (
                <li key={`sum-${re.row}-${re.email}`}>
                  <span className="font-medium text-text-default">{re.nombre}</span> ({re.email}) — Fila {re.row}:{' '}
                  {re.errors.join('; ')}
                </li>
              ))}
            </ul>
          ) : null}
          {bulkImportSummary.createdUsers.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="default"
                className="gap-2"
                onClick={() => downloadBulkCreatedUsersExcel(bulkImportSummary.createdUsers)}
              >
                <Download className="size-4" aria-hidden />
                Descargar Excel con credenciales
              </Button>
            </div>
          ) : null}
          <p className="text-xs text-text-subtle m-0">
            Las contraseñas viajan cifradas por HTTPS y solo existen en este resultado y en el archivo que descargues;
            guárdalas de forma segura y no las compartas.
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => resetAll()}>
            Cerrar resumen y empezar de nuevo
          </Button>
        </div>
      )}

      {activeStep === 0 && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border-subtle bg-bg-surface-alt/50 p-4 text-center">
              <p className="text-sm font-semibold text-text-default m-0 mb-1">Descargar Template</p>
              <p className="text-xs text-text-subtle mb-3 m-0 min-h-10">
                Columnas alineadas al alta manual en lidz_front: nombre, email, teléfono, id externo, asignable.
              </p>
              <Button
                type="button"
                variant="default"
                className="w-full gap-2"
                onClick={() => downloadUsersBulkTemplate()}
              >
                <Download className="size-4" />
                Descargar
              </Button>
            </div>
            <div className="rounded-lg border border-border-subtle bg-bg-surface-alt/50 p-4 text-center">
              <p className="text-sm font-semibold text-text-default m-0 mb-1">Guía de Campos</p>
              <p className="text-xs text-text-subtle mb-3 m-0 min-h-10">Descripción de columnas del archivo</p>
              <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setFieldGuideOpen(true)}>
                <HelpCircle className="size-4" />
                Ver campos
              </Button>
            </div>
          </div>

          {!selectedFile ? (
            <div
              role="presentation"
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
              className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
                isDragging
                  ? 'border-primary bg-muted/40'
                  : 'border-border-subtle hover:border-primary/60 hover:bg-muted/20'
              }`}
            >
              <CloudUpload className="size-12 mx-auto mb-3 text-primary" aria-hidden />
              <p className="text-base font-medium text-text-default m-0 mb-2">Arrastra tu archivo acá</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                className="sr-only"
                aria-label="Seleccionar archivo Excel"
                onChange={handleFileInput}
              />
              <Button type="button" variant="secondary" className="gap-2" onClick={() => fileInputRef.current?.click()}>
                Subir archivo
              </Button>
              <p className="text-xs text-text-subtle mt-4 m-0">Formatos: XLS, XLSX</p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-subtle p-4">
              <div>
                <p className="text-sm font-medium text-text-default m-0">{selectedFile.name}</p>
                <p className="text-xs text-text-subtle m-0 mt-0.5">
                  {(selectedFile.size / 1024).toFixed(1)} KB · {previewRows.length} fila(s)
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" className="gap-2 text-destructive" onClick={resetFile}>
                <Trash2 className="size-4" />
                Limpiar
              </Button>
            </div>
          )}

          {isParsing && <p className="text-sm text-text-subtle m-0">Procesando archivo…</p>}
          {parseError && <p className="text-sm text-destructive m-0">{parseError}</p>}
        </div>
      )}

      {activeStep === 1 && (
        <div className="space-y-3">
          <p className="text-sm text-text-subtle m-0">
            Vista previa de las filas a crear. Usan el proyecto maestro elegido en el sidebar. Marca filas y aplica un
            rol o un subproyecto por vez a las seleccionadas. Se envían al crear usuarios.
          </p>
          {isOccupancyLoading ? <Skeleton className="h-16 w-full" /> : null}
          {isOccupancyError ? (
            <p className="text-sm text-destructive m-0" role="status">
              No se pudieron cargar las cuentas existentes. Podés seguir, pero no vamos a avisar si un correo ya tiene
              usuario en otro proyecto.
            </p>
          ) : null}
          {occupiedOnCurrentProjectCount > 0 ? (
            <Alert variant="destructive">
              <AlertTitle>Correos que ya existen en este proyecto</AlertTitle>
              <AlertDescription>
                {occupiedOnCurrentProjectCount} correo(s) ya tienen usuario acá. Esas filas van a fallar al crear.
              </AlertDescription>
            </Alert>
          ) : null}
          {existingOnOtherProjectsCount > 0 ? (
            <Alert>
              <AlertTitle>Cuentas en otros proyectos</AlertTitle>
              <AlertDescription>
                {existingOnOtherProjectsCount} correo(s) ya tienen usuario en otro proyecto. Van a compartir login y es
                posible que no se genere una contraseña nueva.
              </AlertDescription>
            </Alert>
          ) : null}
          {submitError && (
            <p className="text-sm text-destructive m-0" role="alert">
              {submitError}
            </p>
          )}
          {rowErrors.length > 0 && (
            <ul className="text-xs text-destructive list-disc pl-5 m-0 max-h-32 overflow-y-auto">
              {rowErrors.map((re) => (
                <li key={`r-${re.row}-${re.email}`}>
                  <span className="font-medium">{re.nombre}</span> ({re.email}) — Fila {re.row}: {re.errors.join('; ')}
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-lg border border-border-subtle bg-bg-surface-alt/40 p-4 space-y-3">
            <p className="text-sm font-medium text-text-default m-0">
              Asignación masiva ({selectedRowIds.size} seleccionada(s))
            </p>
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-[minmax(0,33.333%)_auto] sm:items-end">
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="bulk-role-select">Rol a añadir</Label>
                <Select value={roleToApply} onValueChange={setRoleToApply}>
                  <SelectTrigger id="bulk-role-select" className="w-full" size="sm">
                    <SelectValue placeholder="Elegir rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ROLE_SELECT_VALUE}>Elegir rol…</SelectItem>
                    {assignableRoles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={
                  roleToApply === NO_ROLE_SELECT_VALUE || selectedRowIds.size === 0 || assignableRoles.length === 0
                }
                onClick={applyRoleToSelected}
              >
                Añadir rol a seleccionados
              </Button>
            </div>
            {subProjectOptions.length > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="bulk-subproject-select">Subproyecto a añadir</Label>
                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-[minmax(0,33.333%)_auto] sm:items-end">
                  <div className="min-w-0">
                    <Select value={subProjectToApply} onValueChange={setSubProjectToApply}>
                      <SelectTrigger id="bulk-subproject-select" className="w-full" size="sm">
                        <SelectValue placeholder="Elegir subproyecto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_SUBPROJECT_SELECT_VALUE}>Elegir subproyecto…</SelectItem>
                        {subProjectOptions.map((sp) => (
                          <SelectItem key={sp.id} value={String(sp.id)}>
                            {sp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={subProjectToApply === NO_SUBPROJECT_SELECT_VALUE || selectedRowIds.size === 0}
                    onClick={applySubprojectToSelected}
                  >
                    Añadir subproyecto a seleccionados
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-subtle m-0">Este maestro no tiene subproyectos.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={selectedRowIds.size === 0}
                onClick={clearRolesForSelected}
              >
                Quitar roles en seleccionados
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={selectedRowIds.size === 0}
                onClick={clearSubprojectsForSelected}
              >
                Quitar subproyectos en seleccionados
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border-subtle overflow-x-auto max-h-[min(28rem,70vh)] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pr-0">
                    <Checkbox
                      checked={headerCheckboxState}
                      onCheckedChange={handleHeaderSelectAll}
                      aria-label="Seleccionar todas las filas"
                    />
                  </TableHead>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="min-w-[180px]">Inmobiliarias asociadas</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>ID externo</TableHead>
                  <TableHead>Asignable</TableHead>
                  <TableHead className="min-w-[140px]">Roles</TableHead>
                  <TableHead className="min-w-[140px]">Subproyectos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.map((row) => {
                  const meta = rowMeta[row.id] ?? emptyRowMeta();
                  const rolesText =
                    meta.roleIds.length > 0 ? meta.roleIds.map((id) => roleById.get(id) ?? `ID ${id}`).join(', ') : '—';
                  const subsText =
                    meta.subProjectIds.length > 0
                      ? meta.subProjectIds.map((id) => subProjectNameById.get(id) ?? `ID ${id}`).join(', ')
                      : '—';
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="pr-0">
                        <Checkbox
                          checked={selectedRowIds.has(row.id)}
                          onCheckedChange={(c) => toggleRowSelection(row.id, c)}
                          aria-label={`Seleccionar fila ${row.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-text-subtle text-sm">{row.id}</TableCell>
                      <TableCell className="font-medium text-text-default">{row.nombre || '—'}</TableCell>
                      <TableCell className="text-sm">{row.email || '—'}</TableCell>
                      <TableCell className="text-sm align-top">
                        <OccupancyProjectsCell
                          occupancy={occupancyByEmail.get(row.email)}
                          isLoading={isOccupancyLoading}
                          currentProjectId={projectId}
                        />
                      </TableCell>
                      <TableCell className="text-sm">{row.telefono || '—'}</TableCell>
                      <TableCell className="text-sm">{row.idExterno || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {['si', 'sí', 'yes', 'true', '1'].includes(
                          (row.puedeAsignarseClientes || '').trim().toLowerCase()
                        )
                          ? 'Sí'
                          : 'No'}
                      </TableCell>
                      <TableCell className="text-sm align-top">{rolesText}</TableCell>
                      <TableCell className="text-sm align-top">{subsText}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-3 mt-6 pt-4 border-t border-border-subtle">
        <Button type="button" variant="outline" disabled={activeStep === 0} onClick={handleBack} className="gap-1">
          <ChevronLeft className="size-4" />
          Atrás
        </Button>
        {stepFooterTrailingAction}
      </div>

      <Sheet open={fieldGuideOpen} onOpenChange={setFieldGuideOpen}>
        <SheetContent side="right" className="w-[min(100%,56rem)] sm:max-w-4xl">
          <SheetHeader>
            <SheetTitle>Guía de campos</SheetTitle>
            <SheetDescription>Columnas del archivo de carga masiva</SheetDescription>
          </SheetHeader>
          <div className="min-w-0 px-4 pb-4">
            <Table className="table-fixed">
              <colgroup>
                <col className="w-[32%]" />
                <col />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-0 whitespace-normal">Campo</TableHead>
                  <TableHead className="min-w-0 whitespace-normal">Descripción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="min-w-0 align-top font-medium whitespace-normal break-words">nombre</TableCell>
                  <TableCell className="min-w-0 align-top text-sm text-muted-foreground whitespace-normal break-words">
                    Nombre del usuario. Obligatorio. Equivale al campo Nombre del formulario.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="min-w-0 align-top font-medium whitespace-normal break-words">email</TableCell>
                  <TableCell className="min-w-0 align-top text-sm text-muted-foreground whitespace-normal break-words">
                    Correo único (inicio de sesión). Obligatorio.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="min-w-0 align-top font-medium whitespace-normal break-words">
                    telefono
                  </TableCell>
                  <TableCell className="min-w-0 align-top text-sm text-muted-foreground whitespace-normal break-words">
                    Opcional. Igual que Teléfono en el formulario. Alias: phone, phonenumber.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="min-w-0 align-top font-medium whitespace-normal break-words">
                    id_externo
                  </TableCell>
                  <TableCell className="min-w-0 align-top text-sm text-muted-foreground whitespace-normal break-words">
                    Opcional. ID externo. Alias: externalid, external_id.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="min-w-0 align-top font-medium whitespace-normal break-words">
                    puede_asignarse_clientes
                  </TableCell>
                  <TableCell className="min-w-0 align-top text-sm text-muted-foreground whitespace-normal break-words">
                    Opcional. Misma casilla “Puede ser asignado a clientes” del formulario. Valores: si, no, true,
                    false, 1 o 0. Vacío = no.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BulkUserImportWizard;
